import { Box, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import useColors from "../../../api/hooks/useColors";
import { useOnboarding } from "../../../contexts/OnboardingContext";
const LearnBasicsStep = () => {
  const colors = useColors();
  const location = useLocation();
  const { closeOnboardingModal } = useOnboarding();
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);

  // Learning sections with descriptions - memoized to avoid recreating on every render
  const learningSections = useMemo(
    () => [
      {
        title: "Controls & Gaps",
        description:
          "View the gaps and filter them by tool, severity and more.",
        path: "/onboarding/posture/controls",
        navigationPath: "/posture/controls",
        navigationName: "Risk - Controls",
        placeholderColor: "#E3F2FD",
        image: "controls_page.svg",
      },
      {
        title: "Attack Surface",
        description:
          "View detailed visualization of your complete attack surface, mapped to potential risk vectors and threats with remediations for each vector.",
        navigationPath: "/posture/surface",
        navigationName: "Risk - Attack Surface",
        placeholderColor: "#E3F2FD",
        image: "attacksurface_page.svg",
      },

      {
        title: "Inventory",
        description:
          "Get an overview of your inventory or focus your view by filtering source or smart filters to enhance security for threatened entities.",
        path: "/onboarding/inventory/environment",
        navigationPath: "/inventory/environment",
        navigationName: "Assets - Inventory",
        placeholderColor: "#FFF3E0",
        image: "inventory_page.svg",
      },
      {
        title: "Assets",
        description:
          "Auto-created by Breeze — or define your own to focus protection efforts where they matter most.",
        path: "/onboarding/inventory/assets",
        navigationPath: "/inventory/assets",
        navigationName: "Assets - Critical Assets",
        placeholderColor: "#FFF3E0",
        image: "critical_asset_page.svg",
      },
      {
        title: "Integrations",
        description:
          "Connect additional tools to gain a broader view of your environment.",
        path: "/onboarding/inventory/integrations",
        navigationPath: "/inventory/integrations",
        navigationName: "Assets - Integrations",
        placeholderColor: "#E3F2FD",
        image: "integrations_page.svg",
      },
      {
        title: "Settings",
        description:
          "Customize Breeze to suit your team — invite members, set up SSO, and configure notification preferences.",
        path: "/onboarding/account/settings",
        navigationPath: "/settings",
        navigationName: "Account",
        placeholderColor: "#F3E5F5",
        image: "settings_page.svg",
      },
    ],
    []
  );

  // Sync current section index with URL on mount and when location changes
  useEffect(() => {
    const currentPath = location.pathname;
    const sectionIndex = learningSections.findIndex(
      (section) => section.path === currentPath
    );

    if (sectionIndex !== -1 && sectionIndex !== currentSectionIndex) {
      setCurrentSectionIndex(sectionIndex);
    }
  }, [location.pathname, currentSectionIndex, learningSections]);

  const currentSection = learningSections[currentSectionIndex];

  // Generate placeholder UI based on current section
  const renderPlaceholder = (section) => {
    return (
      <Box
        sx={{
          width: "100%",
          height: "100%",
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          cursor: "pointer", // Add cursor pointer to indicate clickability
        }}
        onClick={() => {
          closeOnboardingModal();
        }} // Make the entire placeholder clickable
      >
        <img
          src={`/onboarding/${section.image}`}
          alt={section.title}
          style={{ width: "100%", height: "auto" }}
        />

        {/* Content placeholder */}
        {/* <Box sx={{ p: 2, flexGrow: 1, position: "relative" }}>
          <Box
            sx={{
              position: "absolute",
              bottom: "20px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: colors.primary_main,
              color: "white",
              borderRadius: "20px",
              py: 0.75,
              px: 2,
              display: "flex",
              alignItems: "center",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              cursor: "pointer",
            }}
          >
            <Typography variant="button" sx={{ mr: 1, fontSize: "12px" }}>
              View {section.title}
            </Typography>
            <span style={{ fontSize: "16px" }}>→</span>
          </Box>
        </Box> */}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        position: "relative",
      }}
    >
      {/* Scrollable content area */}
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          pb: 12, // Add bottom padding to account for fixed buttons
        }}
      >
        <Typography variant="body2" sx={{ mb: 2 }}>
          Step 4 of 4
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mr: 1 }}>
            Learn the Basics
          </Typography>
        </Box>
        <Typography variant="body1" sx={{ mb: 2 }}>
          In <strong>{currentSection.title}</strong>,{" "}
          {currentSection.description}
        </Typography>

        {/* Main display */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            mt: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
              borderRadius: "8px",
              border: `1px solid ${colors.grey_10percent}`,
            }}
          >
            {renderPlaceholder(currentSection)}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LearnBasicsStep;
